import React, { useRef } from 'react';

import { isPathMatches } from '~/components/app-header/utils/navigation-utils';
import {
  ChatInputComposer,
  ChatInputComposerProps,
  ChatInputConfig,
} from '~/components/chat';
import { useReasonerParams } from '~/views/alfred/research/hooks/useReasonerParams';
import { postClonePublicConversation } from '~/views/alfred/research/services/conversations-service';
import { useChat } from '~/views/alfred/research/use-chat/useChat';
import { useChatNavigation } from '~/views/alfred/use-chat-navigation/useChatNavigation';

export interface ChatInputProps
  extends Pick<ChatInputComposerProps, 'mode' | 'setMode'> {
  config?: ChatInputConfig;
  submitCallback?: (message?: string) => void;
}

export const ChatDeepAnalysis = ({
  config,
  submitCallback,
  ...rest
}: ChatInputProps) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { navigateToConversation } = useChatNavigation();

  const { conversationId, setReasonerParams } = useReasonerParams();

  const {
    activeConversation,
    postQuestion,
    abortRequest,
    clonePublicConversation,
    appendNewConversation,
  } = useChat(state => ({
    activeConversation: state.getActiveConversation(conversationId),
    postQuestion: state.postQuestion,
    abortRequest: state.abortRequest,
    clonePublicConversation: state.clonePublicConversation,
    appendNewConversation: state.appendNewConversation,
  }));

  const isAlfredPage = isPathMatches(['alfred']);
  const lastTask = activeConversation?.tasks?.at(-1);
  const lastState = lastTask?.states?.at(-1);
  const isLoading =
    isAlfredPage &&
    !!conversationId &&
    (!!lastState?.next || activeConversation?.status === 'Processing');

  const submitHandler = async (
    inputValue: string,
    onSubmitCallbackHandler: () => void
  ) => {
    const question = inputValue.trim();

    if (isLoading || !question) {
      return;
    }

    if (
      activeConversation?.access_level === 'public' ||
      (!activeConversation && conversationId)
    ) {
      const response = await postClonePublicConversation(conversationId);

      if (!response.error) {
        const newConversationId = response.data.conversation_id;

        if (!config?.alfredEverywhere) {
          setReasonerParams({
            conversationId: newConversationId,
          });
        }

        const conversation = await clonePublicConversation(newConversationId);

        if (conversation) {
          appendNewConversation(conversation);
        }

        await postQuestion(
          question,
          isAlfredPage && newConversationId ? newConversationId : undefined
        );

        return;
      }
    }

    const newConversationId = await postQuestion(
      question,
      isAlfredPage && conversationId ? conversationId : undefined
    );

    if (newConversationId && !config?.alfredEverywhere) {
      setReasonerParams({
        conversationId: newConversationId,
      });
    }

    submitCallback?.();
    onSubmitCallbackHandler();

    if (!isAlfredPage && !config?.alfredEverywhere) {
      navigateToConversation(newConversationId ?? '');
    }
  };

  const onSubmit = (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.KeyboardEvent<HTMLTextAreaElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void
  ) => {
    event.preventDefault();
    submitHandler(inputText, onSubmitCallbackHandler);
  };

  const onKeyDown = (
    _: React.KeyboardEvent<HTMLElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void
  ) => {
    submitHandler(inputText, onSubmitCallbackHandler);
  };

  return (
    <ChatInputComposer
      ref={inputRef}
      handlers={{
        onSubmit,
        onKeyDown,
        onSelect: () => {},
        onAbort: () => abortRequest(conversationId),
      }}
      config={{
        ...config,
        isCustomizedDisabled: lastState?.status === 'Fatal',
        isLoading,
      }}
      {...rest}
    />
  );
};
